import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import EstudioConexionResumenProyectoCard from '../../components/estudioConexion/EstudioConexionResumenProyectoCard';
import {
  BloqueVeredictoDual,
  listaTrafosRefuerzosJson
} from '../../components/estudioConexion/estudioConexionResultadoTecnicoShared';
import MitigacionEscenariosEditor, {
  emptyEscenario,
  escenariosDesdeApi,
  medidasParaApi,
  EscenarioMitigacion
} from '../../components/estudioConexion/MitigacionEscenariosEditor';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData, performUncatchedRequest } from '../../utils/request';
import '../../css/PrivateEstudioConexion.css';

function fmtCostoUsd(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `USD ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
}

/** Tabla refuerzos §3.5.2 — fuente `refuerzos_sugeridos_json` (líneas / trafos). */
const MitigacionRefuerzosTabla: FunctionComponent<{
  refuerzosJson?: SimpleObject | null;
}> = ({ refuerzosJson }) => {
  const filas = useMemo(() => {
    if (!refuerzosJson || typeof refuerzosJson !== 'object') return [];
    const lineas = (refuerzosJson.lineas as SimpleObject[] | undefined) ?? [];
    const trafos = listaTrafosRefuerzosJson(refuerzosJson);
    const out: {
      tipo: string;
      id: string;
      capAnt: string;
      capSug: string;
      costo: string;
    }[] = [];
    for (const l of lineas) {
      const tramo = String(l.tramo ?? l.tramo_id ?? l.tramoId ?? '—');
      const a0 = l.ampacidad_actual ?? l.ampacidadActual;
      const a1 = l.ampacidad_sugerida ?? l.ampacidadSugerida;
      const costo = l.costo_estimado_usd ?? l.costoEstimadoUsd;
      out.push({
        tipo: 'Línea',
        id: tramo,
        capAnt: a0 != null ? `${a0} A` : '—',
        capSug: a1 != null ? `${a1} A` : '—',
        costo: fmtCostoUsd(costo)
      });
    }
    for (const t of trafos) {
      const tid = String(t.id ?? t.trafo_id ?? t.trafoId ?? '—');
      const k0 = t.kva_actual ?? t.kvaActual;
      const k1 = t.kva_sugerido ?? t.kvaSugerido;
      const costo = t.costo_estimado_usd ?? t.costoEstimadoUsd;
      out.push({
        tipo: 'Transformador',
        id: tid,
        capAnt: k0 != null ? `${k0} kVA` : '—',
        capSug: k1 != null ? `${k1} kVA` : '—',
        costo: fmtCostoUsd(costo)
      });
    }
    return out;
  }, [refuerzosJson]);

  return (
    <div className='ecg-section'>
      <h3 className='ecg-section-title'>Refuerzos sugeridos (estudio base)</h3>
      <p className='vdr-ecg-lead'>
        Elementos que el estudio base identifica como candidatos a refuerzo. Las medidas de inversión en mitigación
        reutilizan estas recomendaciones cuando aplica.
      </p>
      {filas.length === 0 ? (
        <p className='ecg-hint'>No se identificaron refuerzos sugeridos en este estudio.</p>
      ) : (
        <div className='vdr-result-table-scroll'>
        <table className='ecg-comparacion-table'>
          <thead>
            <tr>
              <th>Tipo de elemento</th>
              <th>ID / tramo</th>
              <th className='vdr-result-num'>Capacidad anterior</th>
              <th className='vdr-result-num'>Capacidad sugerida</th>
              <th className='vdr-result-num'>Costo estimado</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={`${f.tipo}-${f.id}-${i}`}>
                <td>{f.tipo}</td>
                <td style={{ fontFamily: 'var(--vdr-font-mono)', fontSize: '0.86rem' }}>{f.id}</td>
                <td className='vdr-result-num'>{f.capAnt}</td>
                <td className='vdr-result-num'>{f.capSug}</td>
                <td className='vdr-result-num'>{f.costo}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

const PrivateEstudioConexionMitigacionPage: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [estudio, setEstudio] = useState<SimpleObject | null>(null);
  const [resultadosBase, setResultadosBase] = useState<SimpleObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [escenarios, setEscenarios] = useState<EscenarioMitigacion[]>([emptyEscenario()]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const est = await getUncatchEndpointData({ endpoint: `estudios-conexion/${id}` });
        setEstudio(est);
        if (est?.estadoSimulacionId === 2) {
          try {
            const rb = await getUncatchEndpointData({
              endpoint: `estudios-conexion/${id}/resultados-base`
            });
            setResultadosBase(rb);
          } catch {
            setResultadosBase(null);
          }
          try {
            const prev = await getUncatchEndpointData({ endpoint: `estudios-conexion/${id}/mitigaciones` });
            if (Array.isArray(prev) && prev.length > 0) {
              setEscenarios(escenariosDesdeApi(prev as Record<string, unknown>[]));
            } else {
              setEscenarios([emptyEscenario()]);
            }
          } catch {
            setEscenarios([emptyEscenario()]);
          }
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };
    init();
  }, [id]);

  const handleSubmit = async () => {
    if (escenarios.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Agregue al menos un escenario de mitigación.' });
      return;
    }
    setSubmitting(true);
    try {
      const medidas = medidasParaApi(escenarios);

      await toast.promise(
        performUncatchedRequest({
          endpoint: `estudios-conexion/${id}/mitigaciones`,
          method: 'post',
          data: { medidas }
        }),
        { pending: 'Guardando mitigaciones...', success: 'Mitigaciones guardadas', error: 'Error al guardar' }
      );

      await toast.promise(
        performUncatchedRequest({
          endpoint: `estudios-conexion/${id}/run-mitigacion`,
          method: 'post'
        }),
        { pending: 'Ejecutando evaluación...', success: 'Evaluación en ejecución', error: 'Error al ejecutar' }
      );

      navigate(`/private/estudio-conexion/${id}/resultado-mitigacion`);
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.details || error.message });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className='ecg-page-container' style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!estudio || estudio.estadoSimulacionId !== 2) {
    return (
      <div className='ecg-blocked-container'>
        <h2 className='ecg-blocked-title'>Configuración no disponible por ahora</h2>
        <p className='ecg-blocked-text'>
          Para configurar medidas de mitigación, el estudio base debe haber finalizado exitosamente.
        </p>
        <div className='vdr-ecg-blocked-actions'>
          <button className='primary-button-style vdr-ecg-cta' onClick={() => navigate(`/private/estudio-conexion/${id}/resultado`)}>
            Ir a resultados del estudio base
          </button>
          <button className='secondary-button-style vdr-ecg-back' onClick={() => navigate('/private/estudios')}>
            Volver a Mis estudios
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='ecg-page-container'>
      <div className='ecg-title-container vdr-page-head'>
        <div className='vdr-ecg-heading vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>
            Estudio de conexión GD · Mitigaciones · N.º {id}
          </span>
          <h2 className='ecg-title vdr-page-head__title'>Configurar Mitigaciones — {estudio.nombre}</h2>
        </div>
        <div className='vdr-ecg-actions vdr-page-head__actions'>
          <button
            className='secondary-button-style vdr-ecg-back'
            onClick={() => navigate(`/private/estudio-conexion/${id}/resultado`)}>
            Volver a resultados base
          </button>
        </div>
      </div>

      <EstudioConexionResumenProyectoCard estudio={estudio} />

      {resultadosBase?.resultadoJson && (
        <div className='ecg-section ecg-mitigacion-impacto-bloque'>
          <h3 className='ecg-section-title'>Resumen impacto (estudio base)</h3>
          <BloqueVeredictoDual
            data={resultadosBase.resultadoJson as SimpleObject}
          />
        </div>
      )}
      {resultadosBase === null && (
        <div className='ecg-section'>
          <h3 className='ecg-section-title'>Resumen impacto (estudio base)</h3>
          <p className='ecg-hint ecg-hint-warn'>
            No se pudieron cargar los resultados del estudio base desde el servidor. Use &quot;Volver a resultados
            base&quot; arriba o intente más tarde.
          </p>
        </div>
      )}
      {resultadosBase !== null && !resultadosBase.resultadoJson && (
        <div className='ecg-section'>
          <h3 className='ecg-section-title'>Resumen impacto (estudio base)</h3>
          <p className='ecg-hint ecg-hint-warn'>
            No hay resultado técnico detallado disponible para este estudio. Revise los resultados base o vuelva más
            tarde si el motor aún no ha persistido <code>resultado_json</code>.
          </p>
        </div>
      )}

      <MitigacionRefuerzosTabla refuerzosJson={resultadosBase?.refuerzosSugeridosJson as SimpleObject | null | undefined} />

      <MitigacionEscenariosEditor
        bateriaEnProyecto={!!estudio.bateriaIncluida}
        potenciaNominalProyectoKw={
          estudio.potenciaKw !== undefined && estudio.potenciaKw !== null
            ? Number(estudio.potenciaKw)
            : undefined
        }
        tipoGd={estudio.tipoGd === 'EG' ? 'EG' : 'PMGD'}
        value={escenarios}
        onChange={setEscenarios}
      />

      <div className='ecg-actions'>
        <button
          className='primary-button-style vdr-ecg-cta'
          onClick={handleSubmit}
          disabled={submitting}
          style={{ minWidth: 300 }}>
          {submitting ? <CircularProgress size={20} sx={{ color: 'var(--vdr-ink)' }} /> : 'Ejecutar evaluación de mitigaciones'}
        </button>
      </div>
    </div>
  );
};

export default PrivateEstudioConexionMitigacionPage;
